import React, { useCallback, useState } from 'react'
import ModalPayment from '../../Elements/cart/ModalPayment'
import { useDispatch, useSelector } from 'react-redux'
import { setPickupMonitorSelectedOrder, sepPickupMonitorShowPaymentModal, setPickupMonitorShowOrderDataModal, setPickupMonitorShowCancelOrder, setPickupMonitorShowOrderModal } from '../../../redux/action/pickupMonitor'
import { selectIsPaid } from '../../../redux/selector/pickupMonitor'
import { confirmOrderPaymentsThunk } from '../../../redux/thunk/order'
import { roundPrice } from '../../../helper'
import { updateOrderCustomerThunk } from '../../../redux/thunk/order/updateOrderCustomer'
import { getGiftCouponsPayment, getGiftCouponsSum } from '../../../helper/getGiftCouponsPayment'
import { getPrepaidPayments, getPrepaidSum } from '../../../helper/getPrepaidPayments'
import { selectUsername } from '../../../redux/selector/system'
import ModalOptions from './options'
import { updateOrderPickupStatusThunk } from '../../../redux/thunk/pickupMonitor'
import OrderDataModal from '../../Elements/modal/order/OrderDataModal'
import ModalOrder from './order'
import PrintReceiptConfirmationModal from '../../Elements/modal/PrintReceiptConfirmationModal'
import CancelOrder from './cancel'
import CancelInvoiceOrderModal from '../../Elements/modal/order/CancelInvoiceOrderModal'
import { isInvoiceOrder } from '../../../hooks/useCancellationWindow/helper'
import { setOrderStatusThunk } from '../../../redux/thunk/order/setOrderStatus'

const PickupMonitorModals = React.memo(() => {
	const dispatch = useDispatch()
	const showPaymentMethod = useSelector(store => store.pickupMonitor.modal.showPaymentMethod)
	const selectedOrder = useSelector(store => store.pickupMonitor.selectedOrder)
	const showOrderModal = useSelector(store => store.pickupMonitor.modal.showOrder)
	const showOrderDataModal = useSelector(store => store.pickupMonitor.modal.showOrderData)
	const showCancelOrder = useSelector(store => store.pickupMonitor.modal.showCancelOrder)
	const isPaid = useSelector(selectIsPaid)
	const totalSum = (isPaid && selectedOrder?.tips) ? roundPrice(selectedOrder?.totalPrice - selectedOrder?.tips) : selectedOrder?.totalPrice
	const orderPayment = selectedOrder?.payments?.filter(item => item?.method === selectedOrder?.paymentMethod)
	const prepaidPayments = getPrepaidPayments(selectedOrder)
	const totalSumWithoutPrepaid = roundPrice(totalSum - getPrepaidSum(selectedOrder))

	const qrCodePaymentSession = useSelector(state => state.cartPayment.qrCodePaymentSession)
	const paymentSessionLink = qrCodePaymentSession?.link
	const username = useSelector(selectUsername)

	const [shouldCompleteOrder, setShouldCompleteOrder] = useState(false)

	const closeModalPayment = useCallback(() => {
		dispatch(sepPickupMonitorShowPaymentModal(false))
	}, [dispatch])

	const updateOrderStatus = useCallback(() => {
		dispatch(updateOrderPickupStatusThunk(selectedOrder?.orderId, 'PICKED_UP'))
	}, [dispatch, selectedOrder?.orderId])

	const handlePaymentSuccess = useCallback(() => {
		if (shouldCompleteOrder) {
			updateOrderStatus()
			setShouldCompleteOrder(false)
		}
		dispatch(setPickupMonitorSelectedOrder(null))
	}, [dispatch, updateOrderStatus, shouldCompleteOrder])

	const reOpenPaymentModal = useCallback(() => {
		dispatch(sepPickupMonitorShowPaymentModal(false))
	}, [dispatch])

	const handlePayment = async ({ paymentId, paymentMethod, tips = 0, giftCoupons: giftCouponsAsPayment, customer }) => {
		const isEcTerminalPaymentMethod = paymentMethod === 'EC_TERMINAL'
		const isTap2PayPaymentMethod = paymentMethod === 'TAP_TO_PAY'
		const isGiftCouponPayment = paymentMethod === 'GIFT_COUPON'
		const isQrCodePayment = paymentMethod === 'QR_CODE'
		const isInvoicePayment = paymentMethod === 'INVOICE'

		if (isInvoicePayment) {
			await dispatch(updateOrderCustomerThunk(selectedOrder.orderId, customer))
		}
		const paymentWithCoupons = getGiftCouponsPayment(giftCouponsAsPayment)
		const resentPayments = [...paymentWithCoupons, ...prepaidPayments]
		const giftCouponSum = getGiftCouponsSum(giftCouponsAsPayment)
		const orderPaymentId = (isEcTerminalPaymentMethod || isQrCodePayment || isTap2PayPaymentMethod) ? paymentId : orderPayment[0].id
		const paymentLink = isQrCodePayment ? paymentSessionLink : null
		const payments = isGiftCouponPayment ? resentPayments : [{ id: orderPaymentId, method: paymentMethod, total: roundPrice(totalSumWithoutPrepaid - giftCouponSum), link: paymentLink }, ...resentPayments]
		const result = await dispatch(confirmOrderPaymentsThunk(selectedOrder.orderId, { payments, tips, printerMetaData: { userPrintRequest: { userName: username } } }, { module: selectedOrder?.module?.moduleType, businessScope: selectedOrder?.orderType, platformType: selectedOrder?.platformMetaData?.platformType }))

		if (!isEcTerminalPaymentMethod && !isTap2PayPaymentMethod) {
			if (shouldCompleteOrder && result) {
				updateOrderStatus()
			}
			closeModalPayment()
		}
	}

	const closeModalOrderData = useCallback(() => {
		dispatch(setPickupMonitorShowOrderDataModal(false))
	}, [dispatch])

	const closeCancelInvoiceOrderModal = useCallback(() => {
		dispatch(setPickupMonitorShowCancelOrder(false))
		dispatch(setPickupMonitorShowOrderModal(true))
	}, [dispatch])

	const handleCancelInvoiceOrder = useCallback(async (reason, reasonCode) => {
		await dispatch(setOrderStatusThunk(selectedOrder, 'CANCELED', reason, reasonCode))
		dispatch(setPickupMonitorShowCancelOrder(false))
		dispatch(setPickupMonitorShowOrderModal(false))
	}, [dispatch, selectedOrder])

	return (
		<>
			{showOrderModal &&
				<ModalOrder />}
			{!isInvoiceOrder(selectedOrder) &&
				<CancelOrder />}
			<CancelInvoiceOrderModal isOpen={showCancelOrder && isInvoiceOrder(selectedOrder)}
												close={closeCancelInvoiceOrderModal}
												onConfirm={handleCancelInvoiceOrder}
												order={selectedOrder} />
			<OrderDataModal order={selectedOrder}
											modalIsOpen={showOrderDataModal}
											close={closeModalOrderData} />
			<ModalOptions setShouldCompleteOrder={setShouldCompleteOrder} />
			<ModalPayment isOpen={showPaymentMethod}
										closeModal={closeModalPayment}
										openModal={reOpenPaymentModal}
										totalSum={isPaid ? totalSum : totalSumWithoutPrepaid}
										handlePayment={handlePayment}
										updateOrderStatus={updateOrderStatus}
										remark={selectedOrder?.remark}
										isDeferredPayment={!isPaid}
										module={selectedOrder?.module?.moduleType}
										orderType={selectedOrder?.orderType}
										platformType={selectedOrder?.platformMetaData?.platformType}
										resetCart={handlePaymentSuccess}
										customerOrder={selectedOrder?.customer}
										changeCalculatorIsNeeded={!isPaid} />
			<PrintReceiptConfirmationModal />
		</>
	)

})
PickupMonitorModals.displayName = 'PickupMonitorModals'
export default PickupMonitorModals
